/**
 * KB-META
 * Purpose: App config
 * Role: ui.module
 * EntryPoints: 
 * Inputs: 
 * Outputs: 
 * Validation: 
 * Dependencies: 
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=src/app.config; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, APP_INITIALIZER, inject } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, Router, NavigationError } from '@angular/router';
import Aura from '@primeng/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';
import { AuthInterceptor } from './app/guard/auth.interceptor';
import { AnalyticsHttpInterceptor } from './app/guard/analytics-http.interceptor';
import { RunawayApiMonitorInterceptor } from './app/guard/runaway-api-monitor.interceptor';
import { ApiSchemaCaptureInterceptor } from './app/guard/api-schema-capture.interceptor';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MessageService, ConfirmationService } from 'primeng/api';
import { GlobalErrorHandler } from './app/handler/global-error-handler';
import { TourService } from 'ngx-ui-tour-primeng';
import { CentralizedTourService } from './app/services/centralized-tour.service';
import { BreadcrumbService } from './app/services/breadcrumb.service';
import { AnalyticsService } from './app/services/analytics.service';
import { io } from 'socket.io-client';
import { SOCKET_IO } from './app/tokens/socket-io.token';
// import { ENV } from './app/config/environment.prod';
import { environment } from './environments/environment';
import { CryptoService } from './app/services/crypto.service';
import { SessionService } from './app/services/session.service';

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }), withEnabledBlockingInitialNavigation()),
        provideAnimationsAsync(),
        provideHttpClient(
            withInterceptorsFromDi(),
            withFetch()
          ),
          JwtHelperService,
          {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
          },
          {
            provide: HTTP_INTERCEPTORS,
            useClass: AnalyticsHttpInterceptor,
            multi: true,
          },
          ...(!environment.production
            ? [
                {
                  provide: HTTP_INTERCEPTORS,
                  useClass: ApiSchemaCaptureInterceptor,
                  multi: true
                },
                {
                  provide: HTTP_INTERCEPTORS,
                  useClass: RunawayApiMonitorInterceptor,
                  multi: true
                },
                {
                  provide: HTTP_INTERCEPTORS,
                  useClass: ApiSchemaCaptureInterceptor,
                  multi: true
                }
              ]
            : []),
          {
            provide: APP_INITIALIZER,
            multi: true,
            useFactory: () => {
              const analytics = inject(AnalyticsService);
              return () => {
                analytics.initRouterTracking();
                return Promise.resolve();
              };
            }
          },
          MessageService,
          ConfirmationService,
        //   {
        //     provide: ErrorHandler,
        //     useClass: GlobalErrorHandler
        // },
        providePrimeNG({ theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } } }),
        TourService,
        CentralizedTourService,
        BreadcrumbService,
        {
          provide: APP_INITIALIZER,
          multi: true,
          useFactory: () => {
            const router = inject(Router);
            return () => {
              // Recover from lazy-chunk version mismatches (e.g., after idle + new deploy)
              router.events.subscribe(evt => {
                if (evt instanceof NavigationError) {
                  const raw = (evt as any)?.error;
                  const msg = typeof raw === 'string' ? raw : (raw?.message || String(raw || ''));
                  const isChunkFetchFailure =
                    msg.includes('Failed to fetch dynamically imported module') ||
                    msg.includes('Importing a module script failed') ||
                    msg.includes('ChunkLoadError') ||
                    (msg.includes('Loading module') && msg.includes('text/html'));
                  if (isChunkFetchFailure) {
                    try {
                      const FLAG = '__genus_forced_reload_once__';
                      if ((window as any)[FLAG]) { return; }
                      (window as any)[FLAG] = true;
                    } catch {}
                    location.reload();
                  }
                }
              });
              return Promise.resolve();
            };
          }
        },
        {
          provide: APP_INITIALIZER,
          multi: true,
          useFactory: () => {
            const crypto = inject(CryptoService);
            const session = inject(SessionService);
            return async () => {
              try {
                // Prefer encKey cookie for direct restoration across refresh; fallback to wrapped blob or backend
                const encCookie = document.cookie.split('; ').find(r => r.startsWith('encKey='));
                const encKey = encCookie ? decodeURIComponent(encCookie.split('=')[1]) : '';
                if (encKey) {
                  // Try unwrap from cookie using refresh token
                  const rtCookie = document.cookie.split('; ').find(r => r.startsWith('refreshToken='));
                  const refreshToken = rtCookie ? decodeURIComponent(rtCookie.split('=')[1]) : '';
                  if (!refreshToken || !crypto.unwrapKeyFromCookie(encKey, refreshToken)) {
                    // Fallback: if cookie contained raw key from older version
                    try { crypto.setEncryptionKey(encKey); } catch {}
                  }
                } else {
                  // Try restore from wrapped blob (requires refresh token cookie)
                  const cookie = document.cookie.split('; ').find(r => r.startsWith('refreshToken='));
                  const refreshToken = cookie ? decodeURIComponent(cookie.split('=')[1]) : '';
                  if (refreshToken && crypto.restoreKeyFromSession(refreshToken)) {
                    // restored
                  } else {
                    await crypto.initializeEncryptionKey();
                  }
                }
              } catch {}
            };
          }
        },
        {
            provide: SOCKET_IO,
            useFactory: () => {
                return io(environment.mingleApiUrl, {
                    transports: ['websocket'],
                    autoConnect: false,
                    reconnection: true,
                    reconnectionAttempts: 5,
                    reconnectionDelay: 1000,
                    reconnectionDelayMax: 5000,
                    timeout: 20000,
                    withCredentials: true,
                    path: '/socket.io',
                    forceNew: true,
                    query: {},
                    extraHeaders: {
                        // Add any custom headers if needed
                    }
                });
            }
        }
    ]
};
